<template>
  <div
    class="exports-page flex min-h-0 w-full flex-col py-6 text-gray-900 dark:text-gray-100"
    style="font-family: 'Inter', sans-serif;"
  >
    <header class="exports-page__header mb-6">
      <p class="text-[0.6875rem] font-semibold uppercase tracking-widest text-[color:var(--v-primary-base)]">
        PrediX™
      </p>
      <h1 class="mt-2 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        Exports
      </h1>
      <p class="mt-2 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-400">
        PrediX reports you created. Open a shareable link, copy a password, or remove an export.
      </p>
    </header>

    <div
      v-if="loading"
      class="exports-panel flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-12 shadow-sm dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none"
    >
      <div class="exports-spinner" />
      <p class="text-sm text-gray-500 dark:text-gray-400">Loading exports…</p>
    </div>

    <div
      v-else-if="error"
      class="exports-panel flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center shadow-sm dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none"
    >
      <i class="fa-regular fa-circle-xmark text-2xl text-red-500" aria-hidden="true" />
      <p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
      <CTailwindBtn type="button" variant="outline" @click="load(page)">Try again</CTailwindBtn>
    </div>

    <div
      v-else-if="!reports.length"
      class="exports-panel flex min-h-64 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300/90 bg-white/80 px-6 py-12 text-center dark:border-white/15 dark:bg-white/[0.02]"
    >
      <div class="exports-empty-icon mb-1">
        <i class="fa-regular fa-folder-open" aria-hidden="true" />
      </div>
      <p class="text-sm font-medium text-gray-800 dark:text-gray-200">No exports yet</p>
      <p class="max-w-md text-sm leading-6 text-gray-500 dark:text-gray-400">
        Create one from PrediX → Export after running a prediction.
      </p>
      <router-link class="mt-3" to="/panel/predix">
        <CTailwindBtn type="button" variant="primary" tag="span">
          Open PrediX
        </CTailwindBtn>
      </router-link>
    </div>

    <div
      v-else
      class="exports-panel overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/[0.03] dark:shadow-none"
    >
      <div class="exports-meta flex items-center justify-between gap-3 border-b border-gray-100 dark:border-white/10">
        <div class="text-sm font-medium text-gray-600 dark:text-gray-300">
          {{ totalCount }} export{{ totalCount === 1 ? '' : 's' }}
          <span v-if="totalPages > 1" class="text-gray-400 dark:text-gray-500">
            · page {{ page }} of {{ totalPages }}
          </span>
        </div>
        <button
          type="button"
          class="exports-refresh"
          title="Refresh"
          @click="load(page)"
        >
          <i class="fa-solid fa-arrows-rotate" aria-hidden="true" />
          Refresh
        </button>
      </div>

      <div class="exports-list">
        <div
          v-for="report in reports"
          :key="reportId(report)"
          class="exports-row"
        >
          <div class="exports-row__info">
            <h2 class="exports-row__title">
              {{ reportName(report) }}
            </h2>

            <a
              class="exports-row__link"
              :href="reportHref(report)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
              <span>{{ reportHref(report) }}</span>
            </a>

            <div
              v-if="reportPassword(report)"
              class="exports-row__password"
            >
              <span>Password</span>
              <code class="exports-password-value">{{ reportPassword(report) }}</code>
              <button
                type="button"
                class="exports-icon-btn"
                :title="copiedId === reportId(report) ? 'Copied' : 'Copy password'"
                @click="copyPassword(report)"
              >
                <i
                  class="fa-solid"
                  :class="copiedId === reportId(report) ? 'fa-check' : 'fa-copy'"
                  aria-hidden="true"
                />
                {{ copiedId === reportId(report) ? 'Copied' : 'Copy' }}
              </button>
            </div>
          </div>

          <div class="exports-row__actions">
            <a
              class="exports-action-btn"
              :href="reportHref(report)"
              target="_blank"
              rel="noopener noreferrer"
              title="Open report"
            >
              <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
              Open
            </a>
            <button
              type="button"
              class="exports-action-btn exports-action-btn--danger"
              :disabled="deletingId === reportId(report)"
              title="Remove export"
              @click="openRemoveDialog(report)"
            >
              <i
                class="fa-solid"
                :class="deletingId === reportId(report) ? 'fa-spinner fa-spin' : 'fa-trash'"
                aria-hidden="true"
              />
              Remove
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="totalPages > 1"
        class="exports-pagination flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 dark:border-white/10"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Showing {{ pageRangeLabel }}
        </p>
        <div class="flex items-center gap-2">
          <CTailwindBtn
            type="button"
            variant="outline"
            :disabled="loading || page <= 1"
            @click="goToPage(page - 1)"
          >
            Previous
          </CTailwindBtn>
          <CTailwindBtn
            type="button"
            variant="outline"
            :disabled="loading || page >= totalPages"
            @click="goToPage(page + 1)"
          >
            Next
          </CTailwindBtn>
        </div>
      </div>
    </div>

    <ModalBasicTailwind
      id="exports-remove-dialog"
      :modal-open="removeDialogOpen"
      title="Remove export"
      :subtitle="removeDialogSubtitle"
      @close-modal="closeRemoveDialog"
    >
      <div class="px-5 py-4">
        <p class="text-sm leading-6 text-gray-600 dark:text-gray-400">
          This permanently removes
          <span class="font-semibold text-gray-900 dark:text-gray-100">“{{ removeTargetName }}”</span>
          from your exports list. The public link will no longer work.
        </p>
      </div>
      <div class="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700/60">
        <CTailwindBtn
          type="button"
          variant="outline"
          :disabled="!!deletingId"
          @click="closeRemoveDialog"
        >
          Cancel
        </CTailwindBtn>
        <CTailwindBtn
          type="button"
          variant="primary"
          class="exports-remove-confirm"
          :disabled="!!deletingId"
          @click="confirmRemove"
        >
          <i
            class="fa-solid"
            :class="deletingId ? 'fa-spinner fa-spin' : 'fa-trash'"
            aria-hidden="true"
          />
          {{ deletingId ? 'Removing…' : 'Remove export' }}
        </CTailwindBtn>
      </div>
    </ModalBasicTailwind>
  </div>
</template>

<script>
import {
  listMyReports,
  deleteReport,
  extractReportId,
  normalizeReportListPage,
  REPORTS_PAGE_SIZE,
} from '@/api/reports'
import ModalBasicTailwind from '@/components/modal/ModalBasicTailwind.vue'
import CTailwindBtn from '@/components/Vuetify/CTailwindBtn.vue'

export default {
  name: 'ExportsList',
  components: {
    ModalBasicTailwind,
    CTailwindBtn,
  },
  data: () => ({
    loading: true,
    error: '',
    reports: [],
    deletingId: null,
    copiedId: null,
    removeDialogOpen: false,
    removeTarget: null,
    page: 1,
    pageSize: REPORTS_PAGE_SIZE,
    totalCount: 0,
    totalPages: 1,
  }),
  computed: {
    removeTargetName() {
      return this.reportName(this.removeTarget)
    },
    removeDialogSubtitle() {
      return 'This action cannot be undone.'
    },
    pageRangeLabel() {
      if (!this.totalCount) return '0'
      const start = (this.page - 1) * this.pageSize + 1
      const end = Math.min(this.page * this.pageSize, this.totalCount)
      return `${start}–${end} of ${this.totalCount}`
    },
  },
  created() {
    this.load(1)
  },
  methods: {
    reportId(report) {
      return extractReportId(report) || (report && report.id) || ''
    },
    reportName(report) {
      return (report && report.name) || 'Untitled export'
    },
    reportPassword(report) {
      if (!report) return ''
      const raw = report.password || report.pass || ''
      return raw ? String(raw) : ''
    },
    reportHref(report) {
      const id = this.reportId(report)
      if (!id) return '#'
      const route = this.$router.resolve({ name: 'PredixPublicReport', params: { id: String(id) } })
      return `${window.location.origin}${route.href}`
    },
    async copyPassword(report) {
      const password = this.reportPassword(report)
      const id = this.reportId(report)
      if (!password || !id) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(password)
        } else {
          const ta = document.createElement('textarea')
          ta.value = password
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedId = id
        if (this._copyTimer) clearTimeout(this._copyTimer)
        this._copyTimer = setTimeout(() => {
          if (this.copiedId === id) this.copiedId = null
        }, 2000)
        this.$root.notify.show({ message: 'Password copied.', type: 'success' })
      } catch (e) {
        this.$root.notify.show({ message: 'Could not copy password.', type: 'error' })
      }
    },
    goToPage(nextPage) {
      const page = Math.min(Math.max(1, nextPage), this.totalPages || 1)
      if (page === this.page && this.reports.length) return
      this.load(page)
    },
    load(page = this.page) {
      const targetPage = Math.max(1, Number(page) || 1)
      this.loading = true
      this.error = ''
      listMyReports(
        { page: targetPage, page_size: this.pageSize },
        (res) => {
          this.loading = false
          const pageData = normalizeReportListPage(res, targetPage, this.pageSize)
          this.reports = pageData.reports
          this.totalCount = pageData.count
          this.page = pageData.page
          this.pageSize = pageData.pageSize
          this.totalPages = pageData.totalPages
          // If current page is empty after deletes, step back.
          if (!this.reports.length && this.page > 1 && this.totalCount > 0) {
            this.load(this.page - 1)
          }
        },
        (err) => {
          this.loading = false
          const status = err && err.status
          if (status === 401) {
            this.error = 'Please sign in again to view your exports.'
          } else {
            this.error = 'Could not load exports. Please try again.'
          }
        }
      )
    },
    openRemoveDialog(report) {
      const id = this.reportId(report)
      if (!id || this.deletingId) return
      this.removeTarget = report
      this.removeDialogOpen = true
    },
    closeRemoveDialog() {
      if (this.deletingId) return
      this.removeDialogOpen = false
      this.removeTarget = null
    },
    confirmRemove() {
      const report = this.removeTarget
      const id = this.reportId(report)
      if (!id || this.deletingId) return
      this.deletingId = id
      deleteReport(
        id,
        () => {
          this.deletingId = null
          this.removeDialogOpen = false
          this.removeTarget = null
          this.$root.notify.show({ message: 'Export removed.', type: 'success' })
          this.load(this.page)
        },
        (err) => {
          this.deletingId = null
          const status = err && err.status
          let message = 'Could not remove this export.'
          if (status === 403) message = 'You can only remove exports you created.'
          if (status === 404) message = 'Export not found.'
          this.$root.notify.show({ message, type: 'error' })
        }
      )
    },
  },
}
</script>

<style scoped>
.exports-meta {
  padding: 1.1rem 1.35rem;
}

.exports-pagination {
  padding: 1rem 1.35rem;
}

.exports-spinner {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border: 3px solid #d1d5db;
  border-top-color: var(--v-primary-base, #0393a8);
  animation: exports-spin 0.8s linear infinite;
}

html.dark .exports-spinner {
  border-color: rgb(255 255 255 / 0.2);
  border-top-color: var(--v-primary-base, #0393a8);
}

@keyframes exports-spin {
  to {
    transform: rotate(360deg);
  }
}

.exports-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 12%, transparent);
  color: var(--v-primary-base, #0393a8);
  font-size: 1.15rem;
}

.exports-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.3rem 0.55rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.45rem;
  background: #fff;
  color: #4b5563;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.exports-refresh:hover {
  background: #f9fafb;
}

html.dark .exports-refresh {
  border-color: rgb(255 255 255 / 0.12);
  background: transparent;
  color: #d1d5db;
}

html.dark .exports-refresh:hover {
  background: rgb(255 255 255 / 0.06);
}

.exports-password-value {
  font-size: 0.8125rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 500;
  color: #111827;
  background: #f3f4f6;
  border-radius: 0.35rem;
  padding: 0.15rem 0.45rem;
  word-break: break-all;
}

html.dark .exports-password-value {
  color: #f3f4f6;
  background: rgb(255 255 255 / 0.08);
}

.exports-icon-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  padding: 0.2rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.35rem;
  background: #fff;
  color: #374151;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.exports-icon-btn:hover {
  background: #f9fafb;
}

html.dark .exports-icon-btn {
  border-color: rgb(255 255 255 / 0.15);
  background: transparent;
  color: #d1d5db;
}

html.dark .exports-icon-btn:hover {
  background: rgb(255 255 255 / 0.06);
}

.exports-action-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 2.25rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fff;
  color: #374151;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}

.exports-action-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.exports-action-btn--danger {
  border-color: #fecaca;
  color: #dc2626;
}

.exports-action-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
}

.exports-action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

html.dark .exports-action-btn {
  border-color: rgb(255 255 255 / 0.12);
  background: transparent;
  color: #e5e7eb;
}

html.dark .exports-action-btn:hover {
  background: rgb(255 255 255 / 0.06);
}

html.dark .exports-action-btn--danger {
  border-color: rgb(248 113 113 / 0.35);
  color: #f87171;
}

html.dark .exports-action-btn--danger:hover:not(:disabled) {
  background: rgb(248 113 113 / 0.12);
}

.exports-list {
  width: 100%;
}

.exports-row {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  column-gap: 1rem;
  width: 100%;
  box-sizing: border-box;
  padding: 1rem 1.35rem;
  border-bottom: 1px solid #f3f4f6;
}

.exports-row:last-child {
  border-bottom: 0;
}

html.dark .exports-row {
  border-bottom-color: rgb(255 255 255 / 0.1);
}

.exports-row:hover {
  background: color-mix(in srgb, var(--v-primary-base, #0393a8) 4%, transparent);
}

html.dark .exports-row:hover {
  background: rgb(255 255 255 / 0.03);
}

.exports-row__info {
  min-width: 0;
  overflow: hidden;
}

.exports-row__title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

html.dark .exports-row__title {
  color: #f3f4f6;
}

.exports-row__link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.375rem;
  min-width: 0;
  font-size: 0.875rem;
  color: var(--v-primary-base, #0393a8);
  text-decoration: none;
}

.exports-row__link:hover {
  text-decoration: underline;
}

.exports-row__link i {
  flex: 0 0 auto;
  font-size: 0.75rem;
}

.exports-row__link span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exports-row__password {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

html.dark .exports-row__password {
  color: #9ca3af;
}

.exports-row__actions {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  grid-column: 2;
}

.exports-remove-confirm {
  background-color: #dc2626 !important;
  border-color: #dc2626 !important;
  color: #fff !important;
}

.exports-remove-confirm:hover:not(:disabled) {
  filter: brightness(0.95);
}
</style>
